package diana.soleil.design.iraniancanadienbazzar.feature.onboarding

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.PasswordStrengthMeter
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BrandMark
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppleLogo
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.GoogleLogo
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.common.Validators
import diana.soleil.design.iraniancanadienbazzar.core.common.PasswordStrength
import diana.soleil.design.iraniancanadienbazzar.core.common.passwordStrength
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_apple
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_divider
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_email_label
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_email_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_google
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_logo_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_password_label
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_password_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signin_show_password_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_confirm_label
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_confirm_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_have_account
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_name_label
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_name_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_email_invalid
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_password_hint
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_strength_weak
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_strength_fair
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_strength_strong
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_password_mismatch
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_sign_in
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_submit
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_subtitle
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.resources.signup_title
import org.jetbrains.compose.resources.stringResource

/** Minimum password length accepted by the form (Firebase email/password requires ≥ 6). */
private const val MIN_PASSWORD = 6

/**
 * Dedicated registration screen (owner direction 2026-07-13). Name + email + password + confirm,
 * with local validation, then hands off to [onSubmit]. Social + "already have an account" mirror
 * the Sign In screen so the two read as one flow. Copy-free of auth logic — the route owns that.
 */
@Composable
fun SignUpScreen(
    onSubmit: (name: String, email: String, password: String) -> Unit,
    // Null hides the button: the social credential flows aren't wired yet, and a button that
    // does nothing reads as "broken app". Reintroduce per-platform as each flow ships.
    onGoogle: (() -> Unit)?,
    onApple: (() -> Unit)?,
    onSignIn: () -> Unit,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    isLoading: Boolean = false,
    status: SignInStatus? = null,
) {
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var confirm by remember { mutableStateOf("") }
    var passwordVisible by remember { mutableStateOf(false) }

    val emailValid = Validators.isValidEmail(email)
    // Show the email-format error only once they've typed something (don't nag an empty field).
    val emailError = email.isNotBlank() && !emailValid
    val passwordLongEnough = password.length >= MIN_PASSWORD
    val mismatch = confirm.isNotEmpty() && confirm != password
    val canSubmit = name.isNotBlank() && emailValid &&
        passwordLongEnough && confirm == password && !isLoading

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(AppTheme.colors.background)
            .safeContentPadding(),
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.lg),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            Column(
                modifier = Modifier.widthIn(max = AppTheme.sizes.contentMaxWidthCompact).fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Box(
                    modifier = Modifier
                        .background(AppTheme.colors.brandInk, AppTheme.shapes.tile)
                        .padding(AppTheme.spacing.md),
                    contentAlignment = Alignment.Center,
                ) {
                    BrandMark(
                        contentDescription = stringResource(Res.string.signin_logo_a11y),
                        modifier = Modifier.size(AppTheme.sizes.iconHero),
                    )
                }
                Spacer(Modifier.height(AppTheme.spacing.lg))
                Text(
                    text = stringResource(Res.string.signup_title),
                    style = AppTheme.typography.headlineLg,
                    color = AppTheme.colors.primary,
                    textAlign = TextAlign.Center,
                )
                Spacer(Modifier.height(AppTheme.spacing.sm))
                Text(
                    text = stringResource(Res.string.signup_subtitle),
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                )

                Spacer(Modifier.height(AppTheme.spacing.xl))

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .shadow(AppTheme.elevation.level2, AppTheme.shapes.tile)
                        .background(AppTheme.colors.surface, AppTheme.shapes.tile)
                        .padding(AppTheme.spacing.lg),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                ) {
                    SignUpFieldLabel("person", stringResource(Res.string.signup_name_label))
                    AppTextField(
                        value = name,
                        onValueChange = { name = it },
                        placeholder = stringResource(Res.string.signup_name_placeholder),
                    )

                    SignUpFieldLabel("mail", stringResource(Res.string.signin_email_label))
                    AppTextField(
                        value = email,
                        onValueChange = { email = it },
                        placeholder = stringResource(Res.string.signin_email_placeholder),
                        keyboardType = KeyboardType.Email,
                        isError = emailError,
                        supportingText = stringResource(Res.string.signup_email_invalid).takeIf { emailError },
                    )

                    SignUpFieldLabel("lock", stringResource(Res.string.signin_password_label))
                    AppTextField(
                        value = password,
                        onValueChange = { password = it },
                        placeholder = stringResource(Res.string.signin_password_placeholder),
                        keyboardType = KeyboardType.Password,
                        visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                        trailing = {
                            AppIconButton(
                                iconName = if (passwordVisible) "visibility_off" else "visibility",
                                contentDescription = stringResource(Res.string.signin_show_password_a11y),
                                onClick = { passwordVisible = !passwordVisible },
                                tint = AppTheme.colors.onSurfaceVariant,
                            )
                        },
                    )
                    Text(
                        text = stringResource(Res.string.signup_password_hint, MIN_PASSWORD),
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                    when (passwordStrength(password)) {
                        PasswordStrength.None -> Unit
                        PasswordStrength.Weak -> PasswordStrengthMeter(
                            filled = 1,
                            total = 3,
                            color = AppTheme.colors.error,
                            label = stringResource(Res.string.signup_strength_weak),
                        )
                        PasswordStrength.Fair -> PasswordStrengthMeter(
                            filled = 2,
                            total = 3,
                            color = AppTheme.colors.alert,
                            label = stringResource(Res.string.signup_strength_fair),
                        )
                        PasswordStrength.Strong -> PasswordStrengthMeter(
                            filled = 3,
                            total = 3,
                            color = AppTheme.colors.success,
                            label = stringResource(Res.string.signup_strength_strong),
                        )
                    }

                    SignUpFieldLabel("lock", stringResource(Res.string.signup_confirm_label))
                    AppTextField(
                        value = confirm,
                        onValueChange = { confirm = it },
                        placeholder = stringResource(Res.string.signup_confirm_placeholder),
                        keyboardType = KeyboardType.Password,
                        visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    )
                    if (mismatch) {
                        Text(
                            text = stringResource(Res.string.signup_password_mismatch),
                            style = AppTheme.typography.bodySm,
                            color = AppTheme.colors.alert,
                        )
                    }

                    AppButton(
                        text = stringResource(Res.string.signup_submit),
                        onClick = { onSubmit(name, email, password) },
                        modifier = Modifier.fillMaxWidth(),
                        trailingIcon = "arrow_forward",
                        loading = isLoading,
                        enabled = canSubmit,
                    )

                    if (status != null) StatusBanner(status)

                    // Divider + social — only when at least one wired provider exists.
                    if (onGoogle != null || onApple != null) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Divider(Modifier.weight(1f))
                        Text(
                            text = stringResource(Res.string.signin_divider).uppercase(),
                            style = AppTheme.typography.labelSm,
                            color = AppTheme.colors.onSurfaceVariant,
                            modifier = Modifier.padding(horizontal = AppTheme.spacing.md),
                        )
                        Divider(Modifier.weight(1f))
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg)) {
                        if (onGoogle != null) AppButton(
                            text = stringResource(Res.string.signin_google),
                            onClick = onGoogle,
                            modifier = Modifier.weight(1f),
                            variant = AppButtonVariant.Secondary,
                            enabled = !isLoading,
                            leadingContent = { GoogleLogo() },
                        )
                        if (onApple != null) AppButton(
                            text = stringResource(Res.string.signin_apple),
                            onClick = onApple,
                            modifier = Modifier.weight(1f),
                            variant = AppButtonVariant.Secondary,
                            leadingContent = { AppleLogo() },
                            enabled = !isLoading,
                        )
                    }
                    }
                }

                Spacer(Modifier.height(AppTheme.spacing.xl))

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                ) {
                    Text(
                        text = stringResource(Res.string.signup_have_account),
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                    Text(
                        text = stringResource(Res.string.signup_sign_in),
                        style = AppTheme.typography.labelMd,
                        color = AppTheme.colors.primary,
                        modifier = Modifier.clickable(onClick = onSignIn),
                    )
                }
            }
        }
        // Drawn last so it sits ABOVE the full-screen scroll Column — otherwise the scroll
        // container intercepts the tap and the back button appears dead (owner-reported bug).
        AppIconButton(
            iconName = "arrow_back",
            contentDescription = stringResource(Res.string.signup_back_a11y),
            onClick = onBack,
            modifier = Modifier.align(Alignment.TopStart),
        )
    }
}

@Composable
private fun SignUpFieldLabel(icon: String, text: String) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
        AppIcon(name = icon, size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSurfaceVariant)
        Text(text = text, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurfaceVariant)
    }
}
